import { cn } from "@/lib/utils";
import Image from "next/image";
import ResponsiveCopy from "../../ui/ResponsiveCopy";

// Icon tile (top-left of the card): the brand logo in a rounded dark square,
// matching the Figma popup. Falls back to a monogram so the scaffold looks
// intentional until real logo SVGs are dropped into
// public/images/2026/built-on-modern/ and wired up in the data array.
function IconTile({ tech }) {
  return (
    <div
      className="flex h-[74px] w-[74px] shrink-0 items-center justify-center"
      style={{
        borderRadius: "15px",
        border: "3px solid transparent",
        // dark fill clipped to padding-box; the gradient lives only on the 1px
        // border (border-box) — the standard gradient-border trick.
        background: [
          "linear-gradient(#0d0d0d, #0d0d0d) padding-box",
          "linear-gradient(135deg, #2E8EFF 0%, rgba(46, 142, 255, 0.00) 30%) border-box",
          "linear-gradient(rgba(46, 142, 255, 0.20), rgba(46, 142, 255, 0.20)) border-box",
        ].join(", "),
      }}
    >
      <div className="shrink-0">{tech.icon}</div>
    </div>
  );
}

// The hover/tap popup itself — a frosted glass card with a soft blue glow along
// the bottom edge (per the Figma reference). Purely presentational: TechTree
// owns positioning + show/hide. Fixed width so the parent can clamp it to the
// viewport without measuring text.
//
// The fill is rgba(65,65,65,0.98) rather than the design's white/25: over this
// section's black it composites to the same #404040, but it stops leaning on
// backdrop-filter to stay readable. A backdrop snapshot costs a frame on
// mobile, and while it was missing, 75% of the text behind the card showed
// straight through and read as doubled-up letters. Alphas were measured against
// a fully opaque card with the blur forced off — peak channel error 29 at 0.88,
// 10 at 0.96, 5 at 0.98, which is where a zero-blur frame stops being tellable
// from a blurred one. The blur is decoration now, not legibility.
//
// `onClose` is passed only when the card is anchored (tap / keyboard focus) —
// a cursor-following card on desktop dismisses itself on pointer-leave and
// doesn't need the affordance.
export default function TechPopup({ tech, onClose, closeLabel }) {
  return (
    <div className="relative w-[min(340px,calc(100vw-24px))] overflow-hidden rounded-[16px] border border-white/10 bg-[rgba(65,65,65,0.98)] p-5 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] backdrop-blur-[20px] lg:h-[304px] lg:w-[420px]">
      {/* blue glow hugging the bottom edge */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[50px] bg-[linear-gradient(to_top,rgba(20,54,94,0.7),transparent)]"
      />

      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute right-1 top-1 z-10 flex h-11 w-11 items-center justify-center rounded-full text-white/70 outline-none transition-colors hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-sky-400"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M3 3L13 13M13 3L3 13"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      <div className="relative flex flex-col gap-4">
        <div className={cn("flex items-center gap-4", onClose && "pe-10")}>
          <IconTile tech={tech} />
          <div className="flex flex-col">
            <span className="text-[24px] font-bold leading-tight text-white">
              <ResponsiveCopy desktop={tech.title} mobile={tech.titleMobile} />
            </span>
            <span
              className={cn(tech.role ? "text-xl text-white/55" : "hidden")}
            >
              {tech.role}
            </span>
          </div>
        </div>

        <p className="text-[20px] leading-[25px] text-white/90">
          <ResponsiveCopy desktop={tech.desc} mobile={tech.descMobile} />
        </p>
      </div>
    </div>
  );
}
